import { makeAutoObservable, runInAction } from 'mobx';
import type { RootStore } from './root-store';
// eslint-disable-next-line import/no-cycle
import { PersonalAssetHistoryMobxDto } from '../mobx/dtos/personal-assets/personal-asset-history-mobx-dto';
// eslint-disable-next-line import/no-cycle
import { PersonalAssetHistoryDialogStore } from './personal-asset-history-dialog-store';
import { PersonalAssetWithValueMobxDto } from '../mobx/dtos/personal-assets/personal-asset-with-value-mobx-dto';

export class PersonalAssetHistoryStore {
    rootStore: RootStore;

    personalAssetHistoryDialogStore: PersonalAssetHistoryDialogStore;

    personalAsset: PersonalAssetWithValueMobxDto;

    latestDate?: Date;

    personalAssetHistories: PersonalAssetHistoryMobxDto[] = [];

    limit = Number(process.env.NEXT_PUBLIC_PAGE_LIMIT);

    totalCount = 0;

    page = 1;

    loading = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.personalAssetHistoryDialogStore = new PersonalAssetHistoryDialogStore(this);

        makeAutoObservable(this, {
            rootStore: false,
            personalAssetHistoryDialogStore: false,
        });
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    async fetchPersonalAsset(id: string): Promise<void> {
        this.startLoading();
        try {
            const [fetchedPersonalAsset, fetchedPersonalAssetHistoryList] = await Promise.all([
                this.rootStore.apiClient.personalAssetsApi.personalAssetControllerGet({
                    id,
                }),
                this.rootStore.apiClient.personalAssetsHistoryApi.personalAssetHistoryControllerGetList({
                    personalAssetId: id,
                    limit: this.limit,
                    offset: (this.page - 1) * this.limit,
                }),
                this.rootStore.portfolioStore.fetchPortfolioList(),
            ]);

            runInAction(() => {
                this.personalAsset = PersonalAssetWithValueMobxDto.create(fetchedPersonalAsset);
                const { items, latestDate } = PersonalAssetHistoryMobxDto.createFromArray(
                    fetchedPersonalAssetHistoryList.items,
                );
                this.personalAssetHistories = items;
                this.latestDate = latestDate;
                this.totalCount = fetchedPersonalAssetHistoryList.total;
                this.stopLoading();
            });
        } catch (e) {
            runInAction(() => this.stopLoading());
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        }
    }

    async deletePersonalAssetHistory(id: string): Promise<void> {
        try {
            this.startLoading();
            await this.rootStore.apiClient.personalAssetsHistoryApi.personalAssetHistoryControllerDelete({
                id,
            });
            await this.fetchPersonalAsset(this.personalAsset.id);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    async setPage(page: number, personalAssetId: string): Promise<void> {
        this.page = page;
        await this.fetchPersonalAsset(personalAssetId);
    }

    setPageLimit(limit: number | string | null, personalAssetId: string): void {
        if (limit && !Number.isNaN(limit)) {
            this.limit = +limit;
            this.setPage(1, personalAssetId);
        }
    }

    get pagesCount(): number {
        return Math.ceil(this.totalCount / this.limit) || 0;
    }
}
